import clsx from "clsx";
import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import Input from "./components/Input";
import { Body8 } from "../../Typography/Body";
import {
  propsMap,
  VerificationCodeInputProps,
  VerificationCodeInputRef,
} from "./types";

const VerificationCodeInput = forwardRef<
  VerificationCodeInputRef,
  VerificationCodeInputProps
>(
  (
    {
      allowedCharacters = "alphanumeric",
      ariaLabel,
      autoFocus = true,
      classes,
      disabled,
      hasError,
      errorMessage,
      children, // helper text children
      length = 6,
      placeholder,
      onChange,
    },
    ref,
  ) => {
    if (length < 1) {
      throw new Error("Length should be a number and greater than 0");
    }

    const inputsRef = useRef<Array<HTMLInputElement>>([]);
    const inputProps = propsMap[allowedCharacters];

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (inputsRef.current) {
          inputsRef.current[0].focus();
        }
      },
      clear: () => {
        if (inputsRef.current) {
          for (let i = 0; i < inputsRef.current.length; i++) {
            inputsRef.current[i].value = "";
          }
          inputsRef.current[0].focus();
        }
        onInputUpdate();
      },
    }));

    useEffect(() => {
      if (autoFocus) {
        inputsRef.current[0].focus();
      }
    }, [autoFocus]);

    const onInputUpdate = () => {
      const res = inputsRef.current.map((input) => input.value).join("");
      onChange && onChange(res);
    };

    const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedValue = e.clipboardData.getData("Text");

      let currentInput = 0;

      for (let i = 0; i < pastedValue.length; i++) {
        const pastedCharacter = pastedValue.charAt(i);
        const currentValue = inputsRef.current[currentInput].value;
        if (pastedCharacter.match(inputProps.pattern)) {
          if (!currentValue) {
            inputsRef.current[currentInput].value = pastedCharacter;
            if (inputsRef.current[currentInput].nextElementSibling !== null) {
              (
                inputsRef.current[currentInput]
                  .nextElementSibling as HTMLInputElement
              ).focus();
              currentInput++;
            }
          }
        }
      }
      onInputUpdate();

      e.preventDefault();
    };

    const inputs = Array.from({ length: length }).map((_, i) => (
      <Input
        index={0}
        hasError={hasError}
        key={i}
        onInputUpdate={onInputUpdate}
        onPaste={handleOnPaste}
        {...inputProps}
        type={inputProps.type}
        ref={(el: HTMLInputElement) => {
          inputsRef.current[i] = el;
        }}
        maxLength={1}
        className={classes?.input}
        autoComplete={i === 0 ? "one-time-code" : "off"}
        aria-label={
          ariaLabel
            ? `${ariaLabel}. Character ${i + 1}.`
            : `Character ${i + 1}.`
        }
        disabled={disabled}
        placeholder={placeholder}
      />
    ));

    return (
      <div className="flex justify-center w-full">
        <div className={clsx("flex flex-col gap-1 w-full", classes?.container)}>
          <div className="flex flex-col gap-1">
            <div
              className={clsx("flex justify-between", classes?.inputsContainer)}
            >
              {inputs}
            </div>
            <div className="flex flex-col gap-3.5">
              {/* min height forces gap and makes sure content does not move when error */}

              <Body8 className="text-red-500 max-w-xs flex w-auto min-h-[25px]">
                {hasError && errorMessage}
              </Body8>
              <div
                className={clsx("flex flex-col w-auto", classes?.helperText)}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

VerificationCodeInput.displayName = "MFA Verifcation Code Input";

export default VerificationCodeInput;
